/**
 * useResponsive Hook
 *
 * Centralized responsive design utilities for the Vortx Flux application.
 * Use this hook in any component that needs responsive behavior.
 *
 * Breakpoints (MUI defaults):
 * - xs: 0px+     (extra-small: phones portrait)
 * - sm: 600px+   (small: phones landscape, small tablets)
 * - md: 900px+   (medium: tablets)
 * - lg: 1200px+  (large: desktops)
 * - xl: 1536px+  (extra-large: large desktops)
 *
 * Usage:
 *   const { isMobile, isTablet, isDesktop, breakpoint } = useResponsive();
 *
 *   // Conditional rendering
 *   {!isMobile && <DesktopOnlyComponent />}
 *
 *   // Dynamic styles
 *   sx={{ padding: isMobile ? 1 : 3 }}
 *
 *   // Grid columns
 *   <Grid item xs={12} md={6} lg={4}>
 */

import { useTheme, useMediaQuery } from '@mui/material';

export function useResponsive() {
    const theme = useTheme();

    // Device type booleans
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));      // < 600px
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md')); // 600-899px
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));       // >= 900px
    const isLargeDesktop = useMediaQuery(theme.breakpoints.up('lg'));  // >= 1200px

    // Shorthand for "not mobile" (tablet or larger)
    const isTabletUp = useMediaQuery(theme.breakpoints.up('sm'));      // >= 600px

    // Shorthand for "desktop or larger"
    const isDesktopUp = useMediaQuery(theme.breakpoints.up('md'));     // >= 900px

    // Current breakpoint name
    const isXs = useMediaQuery(theme.breakpoints.only('xs'));
    const isSm = useMediaQuery(theme.breakpoints.only('sm'));
    const isMd = useMediaQuery(theme.breakpoints.only('md'));
    const isLg = useMediaQuery(theme.breakpoints.only('lg'));
    const isXl = useMediaQuery(theme.breakpoints.only('xl'));

    const breakpoint = isXl ? 'xl' : isLg ? 'lg' : isMd ? 'md' : isSm ? 'sm' : 'xs';

    return {
        // Device type checks
        isMobile,
        isTablet,
        isDesktop,
        isLargeDesktop,
        isTabletUp,
        isDesktopUp,

        // Exact breakpoint checks
        isXs,
        isSm,
        isMd,
        isLg,
        isXl,

        // Current breakpoint name
        breakpoint,

        // Theme access for custom queries
        theme,
    };
}

/**
 * Responsive value helper
 * Returns different values based on current breakpoint
 *
 * Usage:
 *   const padding = responsiveValue({ mobile: 1, tablet: 2, desktop: 3 }, isMobile, isTablet);
 */
export function responsiveValue({ mobile, tablet, desktop }, isMobile, isTablet) {
    if (isMobile) return mobile;
    if (isTablet) return tablet ?? desktop;
    return desktop;
}

/**
 * Common responsive patterns as sx prop helpers
 */
export const responsiveStyles = {
    // Hide on mobile
    hideOnMobile: {
        display: { xs: 'none', sm: 'block' }
    },

    // Hide on desktop
    hideOnDesktop: {
        display: { xs: 'block', md: 'none' }
    },

    // Full width on mobile, auto on desktop
    mobileFullWidth: {
        width: { xs: '100%', sm: 'auto' }
    },

    // Responsive padding
    containerPadding: {
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 2, sm: 3 }
    },

    // Responsive typography scale
    headingLarge: {
        fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }
    },

    headingMedium: {
        fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' }
    },

    // Stack on mobile, row on desktop
    stackOnMobile: {
        flexDirection: { xs: 'column', sm: 'row' }
    },

    // Responsive gap
    responsiveGap: {
        gap: { xs: 1, sm: 2, md: 3 }
    }
};

export default useResponsive;
